import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, Ship } from "lucide-react";
import { TrackingTimeline } from "./TrackingTimeline";
import { TrackingEvents } from "./TrackingEvents";
import { TrackingReviewDialog } from "./TrackingReviewDialog";
import { TrackingStageDialog } from "./TrackingStageDialog";
import { TrackingImport } from "./TrackingImport";
import { TrackingReferences } from "./TrackingReferences";
import { TrackingSync } from "./TrackingSync";

const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTs = (d) => (d ? new Date(d).toLocaleString() : "—");

export const TrackingPanel = ({ container, onChanged }) => {
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/containers/${container.id}/tracking`);
      setData(r.data);
      setThreshold(String(r.data.settings.eta_discrepancy_days));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [container.id]);

  useEffect(() => { load(); }, [load, container.updated_at]);

  useEffect(() => {  // page open / refresh: freshness-gated carrier check (no call when checked recently)
    if (container.status === "RECEIVED") return;
    api.post(`/containers/${container.id}/tracking/sync`, { force: false }).then((r) => { if (["UPDATED", "AWAITING_DISPATCH_CONFIRMATION", "ERROR", "REFERENCE_MISMATCH", "NO_CARRIER_EVENTS", "UP_TO_DATE"].includes(r.data.status)) { load(); if (r.data.status === "UPDATED") onChanged?.(); } }).catch(() => {});
  }, [container.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { load(); onChanged?.(); };

  const saveThreshold = async () => {
    setSavingThreshold(true);
    try {
      await api.put("/container-tracking/settings", { eta_discrepancy_days: Number(threshold) });
      toast.success("ETA discrepancy threshold updated — audited");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSavingThreshold(false); }
  };

  if (!data) return null;
  const closed = container.status === "RECEIVED";
  const reported = data.carrier_eta_reported;
  const override = data.eta_confirmed_override;
  const check = data.container_number_check;

  return (
    <section className="mt-10 rounded-md border border-border bg-white" data-testid="tracking-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="flex items-center gap-2">
          <Ship className="h-4 w-4 text-terracotta" />
          <h2 className="text-base font-medium text-charcoal md:text-lg">Shipment tracking</h2>
          {data.review_required && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-900" data-testid="review-required-badge">Review required</span>
          )}
        </div>
        {!closed && container.status !== "IN_MOROCCO" && (
          <div className="flex flex-wrap gap-2">
            <TrackingStageDialog container={container} stages={data.stages} currentStage={data.transit_stage} onDone={refresh} />
            <TrackingImport onDone={refresh} />
          </div>
        )}
      </div>

      <div className="px-5 py-5">
        <TrackingTimeline stages={data.stages} current={data.transit_stage} dates={data.stage_dates} />

        <TrackingReferences key={container.updated_at} container={container} closed={closed} onChanged={refresh} />
        <TrackingSync container={container} sync={data.sync} onChanged={refresh} />

        {data.open_flags.length > 0 && (
          <div className="mt-6 rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="tracking-discrepancy-banner">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Tracking information needs review</p>
                  <ul className="mt-1 space-y-1">
                    {data.open_flags.map((f) => (
                      <li key={f.id} className="text-xs text-amber-900" data-testid={`tracking-flag-${f.code}`}>
                        <span className="font-mono uppercase">{f.code.replace(/_/g, " ")}</span> · {f.message} <span className="text-amber-700">({f.source}, {fmtTs(f.at)})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <TrackingReviewDialog container={container} reported={reported} override={override} flags={data.open_flags} onDone={refresh} />
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { label: "Vessel / voyage", value: container.vessel ? `${container.vessel}${container.voyage ? ` · ${container.voyage}` : ""}` : "—", sub: data.vessel_history?.length ? `Earlier: ${data.vessel_history.map((v) => `${v.vessel}${v.voyage ? ` / ${v.voyage}` : ""}`).join(", ")}` : null, id: "vessel" },
            { label: "Carrier ETA (reported)", value: reported ? `${fmt(reported.value)}` : "No carrier information yet", sub: reported ? `${reported.source} · as of ${fmt(reported.event_time)}${reported.times?.display ? ` · ${reported.times.display}` : ""}${reported.vessel ? ` · ${reported.vessel}` : ""}` : null, id: "carrier-eta-reported" },
            { label: "Miami port ETA (Admin)", value: fmt(container.eta_carrier), sub: override ? `Confirmed override · ${override.by}` : null, id: "eta-carrier-admin", highlight: !!override },
            { label: container.eta_warehouse_kind === "VERIFIED_APPOINTMENT" ? "Warehouse delivery (verified appointment)" : "Warehouse ETA (provisional)", value: fmt(container.eta_warehouse), sub: container.eta_warehouse_kind === "VERIFIED_APPOINTMENT" ? "Confirmed delivery appointment — preserved across carrier ETA changes" : "Miami port ETA + 5 calendar days — recalculates when the carrier ETA changes", id: "eta-warehouse" },
          ].map((f) => (
            <div key={f.id} className={`rounded-md border p-4 ${f.highlight ? "border-terracotta/50 bg-terracotta/5" : "border-border"}`} data-testid={`tracking-card-${f.id}`}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{f.label}</p>
              <p className="mt-2 text-sm font-medium text-charcoal">{f.value}</p>
              {f.sub && <p className="mt-1 text-xs text-warmgrey">{f.sub}</p>}
            </div>
          ))}
        </div>

        {override && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-sand/40 px-4 py-3" data-testid="override-notice">
            <p className="text-xs text-charcoal">
              <span className="font-semibold">Admin-confirmed ETA override</span> · {fmtTs(override.at)} · {override.by} · “{override.reason}”. Carrier or broker updates are recorded but never replace this value.
            </p>
            {!closed && <TrackingReviewDialog container={container} reported={reported} override={override} flags={[]} onDone={refresh} compact />}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-warmgrey">
          <span data-testid="tracking-last-update">Last tracking update: {data.tracking_last_update ? `${fmtTs(data.tracking_last_update.at)} · ${data.tracking_last_update.source}` : "none recorded"}</span>
          {data.poll_plan && (
            <span data-testid="tracking-poll-plan" title={data.poll_plan.reason}>
              Carrier check cadence: {data.poll_plan.cadence_hours ? (data.poll_plan.cadence_hours >= 168 ? "weekly" : "daily") : "stopped"}{data.poll_plan.poll_carrier ? ` · next ${fmtTs(data.poll_plan.next_poll_at)}` : " · review only (no ZIM poll)"} — {data.poll_plan.reason}
            </span>
          )}
          {check && (
            <span data-testid="container-number-check" className={check.iso6346 && !check.valid ? "text-red-700" : ""}>
              Container no. {check.value}: {check.iso6346 ? (check.valid ? "ISO 6346 valid" : check.reason) : "non-ISO reference"}
            </span>
          )}
          <span className="flex items-center gap-2">
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-warmgrey">ETA warning threshold</Label>
            <Input type="number" min="0" max="90" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="h-8 w-16 bg-white text-xs" data-testid="eta-threshold-input" />
            <span>days</span>
            <Button size="sm" variant="outline" onClick={saveThreshold} disabled={savingThreshold || threshold === String(data.settings.eta_discrepancy_days)} data-testid="eta-threshold-save" className="h-8 border-limestone text-xs">
              {savingThreshold ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
            </Button>
          </span>
        </div>

        <TrackingEvents events={data.events} audit={data.audit} />
      </div>
    </section>
  );
};
